/**
 * Layer A — the captured media backing a finding, resolved on demand.
 *
 * A finding stores provenance (asset_id / survey_field_id), not media; the
 * /v1/audit/findings/:id/evidence endpoint resolves it to the asset's photos
 * (equipment) or the AI's source video clip (survey). Evidence is supplementary:
 * a fetch error or an empty result never blocks the finding — we just show
 * "no evidence" rather than an error.
 */
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Play } from "lucide-react";
import { useVideoUrl } from "@/qa/hooks/useVideoUrl";
import { getFindingEvidence } from "./api";
import type { EvidenceItem } from "./types";

const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function ClipPlayer({
  videoUri,
  spans,
  learningPropertyId,
}: {
  videoUri: string;
  spans: Array<{ start_s: number; end_s: number | null }>;
  learningPropertyId?: string | null;
}) {
  const { data: url, isLoading } = useVideoUrl(videoUri, { learningPropertyId });
  const videoRef = useRef<HTMLVideoElement>(null);
  const pendingSeek = useRef<number | null>(null);
  // The URL is just built client-side; whether the stream is actually
  // authorized/available surfaces only when the element loads. Show a message
  // instead of a silent black player on failure.
  const [failed, setFailed] = useState(false);

  // Seek pattern lifted from src/qa/pages/video-survey/QuestionWorkspace.tsx:
  // if metadata isn't loaded yet, defer the seek to onLoadedMetadata.
  const seekTo = (startS: number) => {
    const v = videoRef.current;
    if (!v) return;
    if (v.readyState < HTMLMediaElement.HAVE_METADATA) {
      pendingSeek.current = startS;
      return;
    }
    v.currentTime = startS;
    void v.play().catch(() => {});
  };
  const onMeta = () => {
    const v = videoRef.current;
    if (!v || pendingSeek.current === null) return;
    v.currentTime = pendingSeek.current;
    pendingSeek.current = null;
    void v.play().catch(() => {});
  };

  if (isLoading || !url) return <div className="aspect-video w-full animate-pulse rounded-md bg-muted/30" />;
  if (failed) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-md bg-muted/30 px-4 text-center text-xs text-muted-foreground">
        This clip couldn't be loaded.
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <video
        ref={videoRef}
        src={url}
        controls
        preload="metadata"
        onLoadedMetadata={onMeta}
        onError={() => setFailed(true)}
        className="w-full rounded-md bg-black"
      />
      {spans.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {spans.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => seekTo(s.start_s)}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ring-1 ring-border/40 transition-colors hover:bg-muted/40"
            >
              <Play className="h-3 w-3" />
              {fmtTime(s.start_s)}
              {s.end_s != null ? `–${fmtTime(s.end_s)}` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function FindingEvidence({
  deficiencyId,
  items: provided,
  learningPropertyId,
}: {
  deficiencyId?: string | null | undefined;
  /** Pre-resolved evidence (e.g. from the learning queue) — skips the fetch. */
  items?: EvidenceItem[];
  /**
   * Set on Learning cards: routes clip playback through /v1/learning/video
   * (property-visibility auth) instead of the reviewer-gated /v1/qa-video, so
   * non-reviewer clients can play their own clips.
   */
  learningPropertyId?: string | null;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["finding-evidence", deficiencyId],
    enabled: !!deficiencyId && provided === undefined,
    staleTime: 5 * 60 * 1000,
    queryFn: () => getFindingEvidence(deficiencyId as string),
  });

  if (provided === undefined) {
    if (!deficiencyId) return null;
    if (isLoading) return <div className="aspect-video w-full animate-pulse rounded-md bg-muted/30" />;
    if (error) return null; // supplementary — never block the finding on an evidence fetch error
  }
  const items = provided ?? data?.evidence ?? [];
  if (items.length === 0) {
    return <p className="text-xs text-muted-foreground">No captured evidence linked yet.</p>;
  }

  return (
    <div className="space-y-2">
      {items.map((item: EvidenceItem, idx) =>
        item.kind === "clip" ? (
          <ClipPlayer key={idx} videoUri={item.video_uri} spans={item.spans} learningPropertyId={learningPropertyId} />
        ) : (
          <figure key={idx} className="space-y-1">
            <img
              src={item.url}
              alt={item.caption ?? "Evidence"}
              loading="lazy"
              className="w-full rounded-md object-cover ring-1 ring-border/30"
            />
            {item.caption && <figcaption className="text-xs text-muted-foreground">{item.caption}</figcaption>}
          </figure>
        ),
      )}
    </div>
  );
}
